import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button, Container, Paper, Tab, Tabs, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useCurriculumStore } from '../../stores/curriculumStore';
import { mockBands, mockSets } from './MockData';

type TabValue = 'sets' | 'bands' | 'options';

export const Groupings: React.FC = () => {
  const { optionBlocks, subjects } = useCurriculumStore();

  const [activeTab, setActiveTab] = useState<TabValue>('sets');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  const getSubjectName = (subjectId: string) => {
    const subject = subjects.find((s) => s.id === subjectId);
    return subject?.name || 'Unknown Subject';
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Student Groupings
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />}>
            Add Grouping
          </Button>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab label="Sets" value="sets" />
            <Tab label="Bands" value="bands" />
            <Tab label="Option Blocks" value="options" />
          </Tabs>
        </Paper>

        {/* Sets Tab */}
        {activeTab === 'sets' && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Ability Sets
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ability-based grouping within a year group for specific subjects
            </Typography>

            {mockSets.map((set) => (
              <Box
                key={set.id}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">
                      {getSubjectName(set.subjectId)} - {set.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {set.yearGroupId.replace('-', ' ').toUpperCase()} | Target: {set.targetAbilityLevel}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Size: {set.currentSize}/{set.capacity}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        {/* Bands Tab */}
        {activeTab === 'bands' && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Subject Bands
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Grouping students across multiple subjects
            </Typography>

            {mockBands.map((band) => (
              <Box
                key={band.id}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6">{band.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {band.yearGroupId.replace('-', ' ').toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Subjects: {band.subjects.map((sid) => getSubjectName(sid)).join(', ')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {band.description}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Size: {band.currentSize}/{band.capacity}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}

        {/* Option Blocks Tab */}
        {activeTab === 'options' && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Option Blocks
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Elective subject choices where students select one subject from each block
            </Typography>

            {optionBlocks.map((block) => (
              <Box
                key={block.id}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6">{block.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {block.yearGroupId.replace('-', ' ').toUpperCase()}
                  {block.simultaneous && ' | Taught simultaneously'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Subjects: {block.subjects.map((sid) => getSubjectName(sid)).join(', ')}
                </Typography>
                {block.periods && block.periods.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Periods: {block.periods.map((p) => `${p.day} P${p.period}`).join(', ')}
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default Groupings;
